'use client';

import React, { Suspense } from 'react';
import { Provider } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { CssBaseline } from '@mui/material';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { WagmiProvider } from '@privy-io/wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Deep imports, not the '@/components' barrel: this module is rendered on every
// route, so a barrel import here pulls the entire component graph — charts
// included — into the chunk set of every page, including static text pages.
import { CollateralAutoMigrator } from '@/components/common/CollateralAutoMigrator';
import { EmbeddedWalletMigrator } from '@/components/common/EmbeddedWalletMigrator';
import ErrorBoundary from '@/components/error-boundary/ErrorBoundary';
import { MainLayout } from '@/components/layout/MainLayout';
import { AuthInitializer } from '@/components/specific/auth/AuthInitializer';
import { WalletActivityWatcher } from '@/components/specific/wallet/WalletActivityWatcher';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { SocketProvider } from '@/contexts/SocketContext';
import { ThemeProvider, useTheme } from '@/contexts/ThemeContext';
import { ReferralTracker } from '@/hooks';
import { wagmiConfig } from '@/lib/wagmi';
import { store } from '@/store';
import { darkTheme, lightTheme } from '@/styles/mui-theme';
import { initMixpanel } from '@/utils';
import { PrivyAuthProvider } from './privy-auth-provider';

import '@/i18n/config';

initMixpanel();

import 'react-toastify/dist/ReactToastify.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

const ThemedApp = ({ children }: { children: React.ReactNode }) => {
  const { mode } = useTheme();

  // Use the current theme for MUI
  const theme = mode === 'dark' ? darkTheme : lightTheme;

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <AuthInitializer />
      <WalletActivityWatcher />
      <Suspense fallback={null}>
        <ReferralTracker />
      </Suspense>
      <CollateralAutoMigrator />
      <EmbeddedWalletMigrator />
      <MainLayout>{children}</MainLayout>
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={mode}
        style={{ zIndex: 99999 }}
      />
    </MuiThemeProvider>
  );
};

export function Providers({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <ThemeProvider>
          <LanguageProvider>
            <PrivyAuthProvider>
              <QueryClientProvider client={queryClient}>
                <WagmiProvider config={wagmiConfig}>
                  <ThemedApp>
                    <SocketProvider>{children}</SocketProvider>
                  </ThemedApp>
                </WagmiProvider>
              </QueryClientProvider>
            </PrivyAuthProvider>
          </LanguageProvider>
        </ThemeProvider>
      </Provider>
    </ErrorBoundary>
  );
}
